/**
 * Enterprise team management page (`/enterprise/team`).
 *
 * The team lead sees:
 *  - A "Create team" empty state if they don't have a workspace yet.
 *  - Otherwise: the member roster with each member's allocated/used/
 *    current-balance credits, an "Invite member" CTA, per-member
 *    Allocate / Recall / Remove actions.
 *
 * Members visiting this URL get redirected to the regular `/app` studio
 * since they don't have admin rights over the team.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/apiError";
import TeamHeaderChip from "@/components/TeamHeaderChip";
import MemberRow from "@/components/team/MemberRow";
import InviteDialog from "@/components/team/InviteDialog";
import TransferDialog from "@/components/team/TransferDialog";
import SettingsDialog from "@/components/team/SettingsDialog";
import TeamGalleryTab from "@/components/team/TeamGalleryTab";
import TeamActivityTab from "@/components/team/TeamActivityTab";
import TeamBillingTab from "@/components/team/TeamBillingTab";
import TeamAnalyticsTab from "@/components/team/TeamAnalyticsTab";
import {
  Users, Plus, ChevronLeft, Loader2, Settings,
  GalleryHorizontal, Activity, CreditCard, BarChart3,
} from "lucide-react";

export default function TeamPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [tab, setTab] = useState("roster");  // roster | billing | analytics | gallery | activity
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Bootstrap: pull my workspace, then its member roster.
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wsRes, meRes] = await Promise.all([
        axios.get(`${API}/workspaces/me`),
        axios.get(`${API}/auth/me`).catch(() => ({ data: {} })),
      ]);
      const list = wsRes.data?.workspaces || [];
      const leadList = list.filter((w) => w.is_lead);
      if (leadList.length === 0) {
        setWorkspace(null);
        setMembers([]);
        return;
      }
      const activeId = meRes.data?.active_workspace_id;
      const leadWs =
        (activeId && leadList.find((w) => w.id === activeId)) ||
        leadList[0];
      setWorkspace(leadWs);
      const memRes = await axios.get(`${API}/workspaces/${leadWs.id}/members`);
      setMembers(memRes.data?.members || []);
    } catch (err) {
      console.warn("team load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/signin?next=/enterprise/team"); return; }
    loadAll();
  }, [authLoading, user, loadAll, navigate]);

  // Reload when TeamHeaderChip switches workspaces while the user is
  // already on this page. The chip dispatches a `facetra:workspace-switched`
  // event so we don't need a prop-drill or a global store.
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener("facetra:workspace-switched", handler);
    return () => window.removeEventListener("facetra:workspace-switched", handler);
  }, [loadAll]);

  const createWorkspace = async () => {
    if (!newName.trim()) { toast.error("Pick a team name."); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/workspaces`, { name: newName.trim() });
      toast.success(res.data?.created
        ? "Team created · Enterprise mode active."
        : "Team already exists — opening it.");
      setNewName("");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't create team."));
    } finally {
      setCreating(false);
    }
  };

  const lead = useMemo(() => members.find((m) => m.role === "lead"), [members]);
  const designers = useMemo(() => members.filter((m) => m.role !== "lead"), [members]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#070707] flex items-center justify-center text-white/55 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin text-[#D4AF37]" /> Loading team…
      </div>
    );
  }

  // Empty state — no workspace yet.
  if (!workspace) {
    return (
      <div className="min-h-screen bg-[#070707] text-white px-6 py-10 sm:px-10">
        <button
          onClick={() => navigate("/app")}
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] mb-8"
          data-testid="team-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to studio
        </button>
        <div className="max-w-md mx-auto rounded-2xl border border-white/10 bg-[#0E0E0E] p-8 sm:p-10 mt-12">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Users className="w-3 h-3" /> Enterprise · Phase 1
          </div>
          <h1 className="font-display text-3xl text-white mt-2">Spin up your team.</h1>
          <p className="text-sm text-white/55 mt-2">
            Create a workspace, invite your designers, and push credits to
            them from your own wallet. They design — you control the spend.
          </p>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mt-6 mb-1.5">
            Team name
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Khanna Jewellers · Atelier 2"
            maxLength={80}
            data-testid="team-create-name-input"
            className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
          />
          <button
            onClick={createWorkspace}
            disabled={creating || !newName.trim()}
            data-testid="team-create-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition disabled:opacity-60"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {creating ? "Creating…" : "Create team"}
          </button>
        </div>
      </div>
    );
  }

  // Full team view.
  return (
    <div className="min-h-screen bg-[#070707] text-white px-6 py-10 sm:px-10">
      <button
        onClick={() => navigate("/app")}
        className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] mb-6"
        data-testid="team-back-btn"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> Back to studio
      </button>
      <div className="flex items-start justify-between gap-4 max-w-5xl">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Users className="w-3 h-3" /> Team workspace
          </div>
          <h1 className="font-display text-3xl text-white mt-1" data-testid="team-name">
            {workspace.name}
          </h1>
          <p className="text-xs text-white/55 mt-1">
            {designers.length} designer{designers.length === 1 ? "" : "s"} ·
            you push credits, they spend them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TeamHeaderChip />
          <button
            onClick={() => setSettingsOpen(true)}
            data-testid="team-settings-btn"
            title="Team settings"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-white/65 hover:text-[#D4AF37] hover:border-[#D4AF37]/45 transition"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => setInviteOpen(true)}
            data-testid="team-invite-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#F3CE5A] transition"
          >
            <Plus className="w-3.5 h-3.5" /> Invite designer
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="max-w-5xl mt-6 inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-[#0E0E0E]">
        {[
          { id: "roster",    label: "Roster",        icon: Users },
          { id: "billing",   label: "Billing",       icon: CreditCard },
          { id: "analytics", label: "Analytics",     icon: BarChart3 },
          { id: "gallery",   label: "Team gallery",  icon: GalleryHorizontal },
          { id: "activity",  label: "Activity",      icon: Activity },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`team-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${
              tab === t.id
                ? "bg-[#D4AF37] text-black"
                : "text-white/60 hover:text-white"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "roster" && (
      <>
      {/* Lead summary card */}
      {lead && (
        <div className="max-w-5xl mt-8 rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/8 via-[#1A1306] to-[#0B0B0B] p-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">You · team lead</div>
            <div className="font-display text-lg text-white mt-0.5">{lead.email}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Wallet</div>
            <div className="font-display text-2xl text-[#D4AF37] mt-0.5" data-testid="team-lead-balance">
              {lead.credits_balance.toFixed(lead.credits_balance % 1 === 0 ? 0 : 1)}
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">credits</div>
          </div>
        </div>
      )}

      {/* Member table */}
      <div className="max-w-5xl mt-6 rounded-xl border border-white/10 bg-[#0E0E0E] overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-white/45 border-b border-white/8 bg-[#0A0A0A]">
          <div className="col-span-5">Designer</div>
          <div className="col-span-2 text-right">Allocated</div>
          <div className="col-span-2 text-right">Balance</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {designers.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-white/45" data-testid="team-empty">
            No designers yet — invite your first one.
          </div>
        ) : designers.map((m) => (
          <MemberRow
            key={m.id}
            member={m}
            onTransfer={() => setTransferTarget(m)}
            onRemove={async () => {
              if (!window.confirm(
                `Remove ${m.email}? Any unspent credits this team pushed to them will be returned to your wallet.`,
              )) return;
              try {
                const r = await axios.delete(`${API}/workspaces/${workspace.id}/members/${m.id}`);
                const restored = Number(r?.data?.credits_restored || 0);
                toast.success(
                  restored > 0
                    ? `Member removed. ${restored.toFixed(0)} credit${restored === 1 ? "" : "s"} restored to your wallet.`
                    : "Member removed.",
                );
                await loadAll();
              } catch (err) {
                toast.error(formatApiError(err, "Remove failed."));
              }
            }}
          />
        ))}
      </div>
      </>
      )}

      {tab === "gallery" && (
        <TeamGalleryTab workspaceId={workspace.id} />
      )}

      {tab === "billing" && (
        <TeamBillingTab workspace={workspace} onChanged={loadAll} />
      )}

      {tab === "analytics" && (
        <TeamAnalyticsTab workspaceId={workspace.id} />
      )}

      {tab === "activity" && (
        <TeamActivityTab workspaceId={workspace.id} />
      )}

      {inviteOpen && (
        <InviteDialog
          workspaceId={workspace.id}
          onClose={() => setInviteOpen(false)}
          onSent={async () => { setInviteOpen(false); await loadAll(); }}
        />
      )}
      {transferTarget && (
        <TransferDialog
          workspaceId={workspace.id}
          member={transferTarget}
          leadBalance={lead?.credits_balance ?? 0}
          onClose={() => setTransferTarget(null)}
          onDone={async () => { setTransferTarget(null); await loadAll(); }}
        />
      )}
      {settingsOpen && (
        <SettingsDialog
          workspace={workspace}
          members={designers}
          onClose={() => setSettingsOpen(false)}
          onChanged={async () => { setSettingsOpen(false); await loadAll(); }}
        />
      )}
    </div>
  );
}
